/* eslint-disable @next/next/no-img-element */
import { observer } from "mobx-react"
import parse from "react-html-parser"
import { useStore } from "@features/common"
import { CareersValue } from "@serializers/careers-value"
import { htmlToTextTransform } from "@lib/html"

import Badge from "./badge.svg"

// This list holds the layout of each corp value, not its words. The columns
// alternate starting on the right, so value 1 sits beside the heading rather than
// under it, and left column values get the wider wrapper.
//
// The titles carry no tilt: the design shows them level, and only the badge
// behind them is rotated.
//
// The badge is tilted in two places, because the shape and its label start from
// different places. Never tilt the wrapper: that would turn both by the same
// amount, which is exactly what does not work here.
//
// badge.svg is drawn with a tilt already in its geometry. Measured off the path's
// own corners, its edges average -1.105deg, so the shape is always 1.105deg
// further round than whatever CSS says.
//
//   badgeClassName      the correction applied to the shape, on top of the
//                       -1.105deg the file already carries
//   badgeTextClassName  the label, which carries nothing, so it needs
//                       shape + (-1.105) to end up parallel to the shape
//
// The pair is written out on each entry below, so the subtraction can be checked
// while reading. Nothing in code enforces it: Tailwind only ships classes it can
// find as literal strings, so the two angles cannot be computed from one number
// without moving both to inline styles, which cannot carry the breakpoint.
//
// Worked out per value rather than taken from the design figures. The reported
// angle gives the size of a tilt but not its direction, so two attempts at a
// formula from those numbers came out wrong: value 3 leans down while value 5
// leans up, though both are reported larger than value 1. Confirmed against the
// design: value 1 has no correction, 3 leans down, 5 leans up. Values 2 and 4 are
// still assumed.

const LAYOUTS = [
  {
    key: "1",
    column: "right" as const,
    fallbackTitle: "Diversity, equity, and inclusivity",
    fallbackDescription:
      "Our culture reflects our app. It’s open-minded, innovative, and authentic. We’ve got people from 34 countries at Pure. And to be clear, you can absolutely be yourself here, just be respectful of others’ preferences.",
    mobileOrderClassName: "order-2",
    wrapperClassName:
      "h-fit w-full flex flex-col items-center tablet:items-start mb-[40px] tablet:mb-[100px] tablet:max-w-[512px]",
    imageClassName:
      "mb-[20px] w-auto h-full max-h-[240px] tablet:max-h-[300px] translate-x-[40px] tablet:translate-x-0",
    // shape 2.605 / 0, label = shape - 1.105 -> 1.5 / -1.105
    badgeClassName: "rotate-[2.605deg] tablet:rotate-0",
    badgeTextClassName: "rotate-[1.5deg] tablet:rotate-[-1.105deg]",
    titleClassName: "w-fit tablet:max-w-[70%] mb-[14px] tablet:mb-[19px]",
    descriptionClassName: "w-fit rotate-[1deg] tablet:rotate-[-2deg] pr-[10px]",
  },
  {
    key: "2",
    column: "left" as const,
    fallbackTitle: "Results over rules",
    fallbackDescription:
      "We trust you to do great work. Deliver what’s agreed upon and enjoy a flexible work style. Your schedule, your location, your way.",
    mobileOrderClassName: "order-1",
    wrapperClassName:
      "h-fit w-full tablet:max-w-[620px] mb-[40px] tablet:mb-[100px]",
    imageClassName:
      "w-auto max-h-[189px] tablet:max-h-[267px] h-auto mb-[20px]",
    // shape 2.605 / 1.167, label = shape - 1.105 -> 1.5 / 0.062
    badgeClassName: "rotate-[2.605deg] tablet:rotate-[1.167deg]",
    badgeTextClassName: "rotate-[1.5deg] tablet:rotate-[0.062deg]",
    titleClassName: "w-fit mb-[14px] tablet:mb-[19px]",
    descriptionClassName: "w-fit rotate-[-1deg] tablet:rotate-[-2deg]",
  },
  {
    key: "3",
    column: "right" as const,
    fallbackTitle: "Your voice counts",
    fallbackDescription:
      "We don’t do rigid hierarchies. Everyone can share ideas and be heard. And if you spot an opportunity, we’ll help you run with it.",
    mobileOrderClassName: "order-4",
    wrapperClassName:
      "h-fit w-full flex flex-col items-center tablet:items-start mb-[40px] tablet:mb-[100px] tablet:max-w-[512px]",
    imageClassName:
      "mb-[20px] w-auto h-auto max-h-[170px] tablet:max-h-[220px] translate-x-[40px] tablet:translate-x-0",
    // shape 2.605 / 0.218, label = shape - 1.105 -> 1.5 / -0.887
    badgeClassName: "rotate-[2.605deg] tablet:rotate-[0.218deg]",
    badgeTextClassName: "rotate-[1.5deg] tablet:rotate-[-0.887deg]",
    titleClassName: "w-fit mb-[14px] tablet:mb-[19px]",
    descriptionClassName: "w-fit rotate-[-1deg] tablet:rotate-[2deg]",
  },
  {
    key: "4",
    column: "left" as const,
    fallbackTitle: "",
    fallbackDescription: "",
    mobileOrderClassName: "order-3",
    wrapperClassName:
      "h-fit w-full flex flex-col items-center tablet:items-start mb-[40px] tablet:mb-0 tablet:max-w-[620px]",
    imageClassName:
      "mb-[20px] w-auto h-auto max-h-[190px] tablet:max-h-[250px] self-start tablet:self-auto",
    // shape 2.605 / 0.704, label = shape - 1.105 -> 1.5 / -0.401
    badgeClassName: "rotate-[2.605deg] tablet:rotate-[0.704deg]",
    badgeTextClassName: "rotate-[1.5deg] tablet:rotate-[-0.401deg]",
    titleClassName: "w-fit mb-[14px] tablet:mb-[19px]",
    descriptionClassName: "w-fit rotate-[-1deg] tablet:rotate-[2deg]",
  },
  {
    key: "5",
    column: "right" as const,
    fallbackTitle: "",
    fallbackDescription: "",
    mobileOrderClassName: "order-5",
    wrapperClassName:
      "h-fit w-full flex flex-col items-center tablet:items-start tablet:max-w-[512px]",
    imageClassName:
      "mb-[20px] w-auto h-auto max-h-[209px] tablet:max-h-[300px]",
    // shape 2.605 / -1.643. The label is deliberately a degree flatter than the
    // shape - 1.105 rule the other four follow, so it reads level here.
    badgeClassName: "rotate-[2.605deg] tablet:rotate-[-1.643deg]",
    badgeTextClassName: "rotate-[2.5deg] tablet:rotate-[-1.748deg]",
    titleClassName: "w-fit mb-[14px] tablet:mb-[19px]",
    descriptionClassName: "w-fit rotate-[1deg] tablet:rotate-[-2deg]",
  },
]

type Layout = typeof LAYOUTS[number]

type ValueBlockProps = {
  layout: Layout
  content: CareersValue
}

const ValueBlock = (props: ValueBlockProps) => {
  const { layout, content } = props

  // parse handles a plain string as well as one carrying a line break, so a
  // title stays flexible whichever source it came from.
  const titleNode = parse(content.title)
  const descriptionNode = parse(content.description)

  return (
    <div
      className={`${layout.mobileOrderClassName} ${layout.wrapperClassName}`}
    >
      {/* The illustration comes from Strapi. The title may carry markup, and a
          screen reader would read that out word for word, so the alt text is the
          stripped version. */}
      {content.image && (
        <img
          src={content.image}
          alt={htmlToTextTransform(content.title)}
          className={layout.imageClassName}
        />
      )}
      <div>
        <p
          className={`${layout.titleClassName} font-quirk-bold text-[24px] leading-[32px] tablet:text-[28px] tablet:leading-[36px] tracking-[0.7px] uppercase`}
        >
          {titleNode}
        </p>
        {/* The badge is optional. A value with no badge text renders no banner,
            rather than an empty lavender shape.

            The banner and its label are tilted separately, never through the
            wrapper, because the two need different angles: the svg is exported
            already tilted, the label is not. See the note on the list above. */}
        {content.badgeText && (
          <div className="relative w-fit mb-[20px] tablet:mb-[30px]">
            <Badge
              viewBox="0 0 173 43"
              preserveAspectRatio="none"
              className={`${layout.badgeClassName} absolute inset-0 w-full h-full pointer-events-none select-none`}
            />
            <p
              className={`${layout.badgeTextClassName} relative px-[18px] py-[10px] font-sans-bold text-[18px] leading-[22px]`}
            >
              {content.badgeText}
            </p>
          </div>
        )}
        <p
          className={`${layout.descriptionClassName} font-sans text-[16px] leading-[20px] tablet:text-[20px] tablet:leading-[32px]`}
        >
          {descriptionNode}
        </p>
      </div>
    </div>
  )
}

const Culture = () => {
  const {
    VacanciesModel: { getTemplate, values: strapiValues },
  } = useStore()

  const title = parse(
    getTemplate("careers.culture.title", false) ||
      '<span class="text-red-300 line-through">Corporate</span><br/>Pure Culture',
  )

  // Each value takes its words from the Strapi entry that carries its key, and
  // from the older flat template keys for anything that entry leaves empty, so
  // the page keeps working while the content moves across. Matching on the key
  // rather than the position means reordering the list in the admin does not
  // hand a value somebody else's column, rotation and mobile order.
  //
  // A value with no title anywhere has nothing to say yet, so it is left out.
  const values = LAYOUTS.flatMap((layout) => {
    const content = strapiValues.find((value) => value.key === layout.key)

    const valueTitle =
      content?.title ||
      getTemplate(`careers.culture.items.${layout.key}.title`, false) ||
      layout.fallbackTitle

    if (!valueTitle) return []

    return [
      {
        layout,
        content: {
          key: layout.key,
          title: valueTitle,
          badgeText:
            content?.badgeText ||
            getTemplate(`careers.culture.items.${layout.key}.badge`),
          description:
            content?.description ||
            getTemplate(
              `careers.culture.items.${layout.key}.description`,
              false,
            ) ||
            layout.fallbackDescription,
          // The legacy keys never carried an image, and nothing is bundled with
          // the frontend, so a value with no upload shows no illustration.
          image: content?.image ?? "",
        },
      },
    ]
  })

  const leftValues = values.filter((value) => value.layout.column === "left")
  const rightValues = values.filter((value) => value.layout.column === "right")

  return (
    <section className="w-full mb-[50px] pt-0 tablet:pt-[100px] laptop:pt-0 tablet:mb-[150px]">
      <div className="flex flex-col tablet:flex-row tablet:gap-[60px] px-[38px] w-full max-w-screen-desktop-l mx-auto laptop:px-[120px]">
        <div className="contents tablet:flex tablet:flex-col tablet:mx-auto">
          <h2 className="order-first h-fit w-fit mb-[40px] tablet:mb-[100px] font-quirk-bold text-[32px] tablet:text-[64px] leading-[40px] tablet:leading-[70px] tracking-[1px] tablet:tracking-[4px] uppercase rotate-[-6.45deg]">
            {title}
          </h2>
          {leftValues.map((value) => (
            <ValueBlock
              key={value.layout.key}
              layout={value.layout}
              content={value.content}
            />
          ))}
        </div>

        <div className="contents tablet:relative tablet:top-[-100px] tablet:flex tablet:flex-col tablet:mx-auto">
          {rightValues.map((value) => (
            <ValueBlock
              key={value.layout.key}
              layout={value.layout}
              content={value.content}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

const CultureComponent = observer(Culture)
export { CultureComponent }
